import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function DeleteAccount() {
    const [activeSection, setActiveSection] = useState<string>("overview");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        reason: "",
        confirm: false,
    });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const scrollTo = (id: string) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            const yOffset = -90;
            const y =
                element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const sections = [
        { id: "overview", title: "1. Overview" },
        { id: "request-form", title: "2. Submit a Deletion Request" },
        { id: "email-option", title: "3. Request via Email" },
        { id: "data-deleted", title: "4. Data That Will Be Deleted" },
        { id: "data-retained", title: "5. Data Retained for Legal Reasons" },
        { id: "timeline", title: "6. Processing Timeline" },
        { id: "contact", title: "7. Contact & Support" },
    ];

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setTimeout(() => {
            setSubmitting(false);
            setSubmitted(true);
        }, 1200);
    };

    return (
        <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
            <LegalHeader />

            {/* Header Title Section */}
            <section className="border-b border-gray-200 py-10 px-6">
                <div className="max-w-[1100px] mx-auto">
                    <p className="text-xs text-gray-500 mb-2">
                        Fixezy / Legal / Account Deletion
                    </p>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                        Account &amp; Data Deletion
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Last updated: October 2026 &nbsp;&bull;&nbsp; Applicable to Fixezy
                        Customer App, Partner App, and fixezy.in
                    </p>
                </div>
            </section>

            {/* Main Content Layout */}
            <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    {/* Table of Contents */}
                    <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
                            Table of Contents
                        </p>
                        <nav className="flex flex-col space-y-2">
                            {sections.map((section) => (
                                <p
                                    key={section.id}
                                    onClick={() => scrollTo(section.id)}
                                    className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                                            ? "text-black font-semibold"
                                            : "text-gray-600 hover:text-black"
                                        }`}
                                >
                                    {section.title}
                                </p>
                            ))}
                        </nav>
                    </aside>

                    {/* Main Content */}
                    <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
                        {/* Section 1 */}
                        <section id="overview" className="scroll-mt-28">
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                1. Overview
                            </h2>
                            <p>
                                Fixezy Private Limited respects your right to control your
                                personal data. In compliance with Google Play Store Developer
                                Program Policies and India's Digital Personal Data Protection
                                (DPDP) Act, 2023, you can request the permanent deletion of
                                your Fixezy account and all associated personal data at any
                                time — even without reinstalling the app.
                            </p>
                            <p className="mt-3">
                                This page provides two methods to submit a deletion request: an
                                online form below or an email to our official support address.
                            </p>
                        </section>

                        {/* Section 2 – Request Form */}
                        <section id="request-form" className="scroll-mt-28">
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                2. Submit a Deletion Request
                            </h2>
                            <p className="mb-4">
                                Fill in the form below with the details of your registered
                                Fixezy account. Our team will verify your identity and process
                                the request within <strong>30 days</strong>.
                            </p>

                            {submitted ? (
                                <div className="border border-green-200 bg-green-50 rounded-lg p-6 text-center space-y-2">
                                    <svg
                                        className="mx-auto w-10 h-10 text-green-500"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={1.8}
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <p className="text-green-800 font-semibold text-base">
                                        Request Submitted Successfully
                                    </p>
                                    <p className="text-green-700 text-sm">
                                        We have received your account deletion request. You will
                                        receive a confirmation email within 48 hours and your data
                                        will be fully deleted within 30 days.
                                    </p>
                                </div>
                            ) : (
                                <form
                                    onSubmit={handleSubmit}
                                    className="border border-gray-200 rounded-lg p-5 bg-gray-50 space-y-4"
                                >
                                    <div>
                                        <label
                                            htmlFor="del-name"
                                            className="block text-xs font-semibold text-gray-700 mb-1"
                                        >
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="del-name"
                                            name="name"
                                            type="text"
                                            required
                                            placeholder="Enter your full name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="del-email"
                                            className="block text-xs font-semibold text-gray-700 mb-1"
                                        >
                                            Registered Email Address{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="del-email"
                                            name="email"
                                            type="email"
                                            required
                                            placeholder="e.g. yourname@email.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="del-phone"
                                            className="block text-xs font-semibold text-gray-700 mb-1"
                                        >
                                            Registered Mobile Number{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="del-phone"
                                            name="phone"
                                            type="tel"
                                            required
                                            placeholder="e.g. +91 98765 43210"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="del-reason"
                                            className="block text-xs font-semibold text-gray-700 mb-1"
                                        >
                                            Reason for Deletion (Optional)
                                        </label>
                                        <select
                                            id="del-reason"
                                            name="reason"
                                            value={formData.reason}
                                            onChange={handleChange}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                                        >
                                            <option value="">-- Select a reason --</option>
                                            <option value="no-longer-using">
                                                No longer using the app
                                            </option>
                                            <option value="privacy-concerns">Privacy concerns</option>
                                            <option value="found-alternative">
                                                Switched to an alternative service
                                            </option>
                                            <option value="too-many-notifications">
                                                Too many notifications
                                            </option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <input
                                            id="del-confirm"
                                            name="confirm"
                                            type="checkbox"
                                            required
                                            checked={formData.confirm}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 border-gray-300 rounded"
                                        />
                                        <label
                                            htmlFor="del-confirm"
                                            className="text-xs text-gray-600"
                                        >
                                            I understand that this action is{" "}
                                            <strong>permanent and irreversible</strong>. My account
                                            and personal data will be permanently deleted, subject to
                                            the legal retention exceptions described on this page.
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full sm:w-auto bg-gray-900 hover:bg-gray-700 disabled:bg-gray-400 text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors"
                                    >
                                        {submitting ? "Submitting…" : "Submit Deletion Request"}
                                    </button>
                                </form>
                            )}
                        </section>

                        {/* Section 3 – Email */}
                        <section id="email-option" className="scroll-mt-28">
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                3. Request via Email
                            </h2>
                            <p>
                                Alternatively, send a deletion request directly to our support
                                team by email:
                            </p>
                            <div className="mt-3 border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2 text-sm text-gray-800">
                                <p>
                                    <strong>Email:</strong>{" "}
                                    <a
                                        href="mailto:support@fixezy.in"
                                        className="text-blue-600 hover:underline"
                                    >
                                        support@fixezy.in
                                    </a>
                                </p>
                                <p>
                                    <strong>Subject Line:</strong> Account Deletion Request
                                </p>
                                <p>
                                    <strong>Include in your email:</strong>
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700 pl-2">
                                    <li>Your full name</li>
                                    <li>
                                        Your registered mobile number or email address on the Fixezy
                                        account
                                    </li>
                                    <li>
                                        A brief statement confirming you wish to permanently delete
                                        your account and data
                                    </li>
                                </ul>
                                <p className="text-xs text-gray-500 pt-1">
                                    We will send an acknowledgment email within 48 hours of
                                    receiving your request.
                                </p>
                            </div>
                        </section>

                        {/* Section 4 – Data Deleted */}
                        <section id="data-deleted" className="scroll-mt-28">
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                4. Data That Will Be Deleted
                            </h2>
                            <p>
                                Upon verifying your identity, the following personal data will
                                be permanently and irreversibly deleted from our active systems:
                            </p>
                            <ul className="list-disc list-inside mt-3 space-y-2 text-gray-700">
                                <li>
                                    <strong>Account credentials:</strong> Registered mobile
                                    number, email address, and OTP authentication records.
                                </li>
                                <li>
                                    <strong>Personal profile:</strong> Full name, profile photo,
                                    and saved preferences.
                                </li>
                                <li>
                                    <strong>Service addresses:</strong> All saved home and office
                                    addresses linked to your account.
                                </li>
                                <li>
                                    <strong>Booking history:</strong> Active and pending booking
                                    records, technician notes, and uploaded appliance photos.
                                </li>
                                <li>
                                    <strong>Communications:</strong> In-app messages, support chat
                                    transcripts, and push notification tokens.
                                </li>
                                <li>
                                    <strong>Payment method tokens:</strong> Tokenized payment
                                    references stored by our gateway (actual card data is never
                                    stored by Fixezy).
                                </li>
                            </ul>
                        </section>

                        {/* Section 5 – Data Retained */}
                        <section
                            id="data-retained"
                            className="scroll-mt-28 border-t border-gray-200 pt-6"
                        >
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                5. Data Retained for Legal / Tax Reasons
                            </h2>
                            <p>
                                Certain records must be retained by Fixezy after account
                                deletion to comply with applicable Indian laws. This data is
                                archived securely and is no longer used for any commercial or
                                marketing purpose:
                            </p>

                            <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
                                <table className="w-full text-left text-xs sm:text-sm">
                                    <thead className="bg-gray-50 text-gray-900 font-semibold border-b border-gray-200">
                                        <tr>
                                            <th className="p-3">Data Type</th>
                                            <th className="p-3">Retention Period</th>
                                            <th className="p-3">Legal Basis</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 text-gray-700">
                                        <tr>
                                            <td className="p-3">GST-compliant tax invoices</td>
                                            <td className="p-3">7 years</td>
                                            <td className="p-3">
                                                Goods and Services Tax Act, 2017 &amp; Income Tax Act,
                                                1961
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-3">Transaction / payment records</td>
                                            <td className="p-3">7 years</td>
                                            <td className="p-3">
                                                Prevention of Money Laundering Act (PMLA), 2002
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-3">
                                                Fraud / dispute investigation logs
                                            </td>
                                            <td className="p-3">
                                                Duration of investigation + 1 year
                                            </td>
                                            <td className="p-3">
                                                IT Act, 2000 &amp; Consumer Protection Act, 2019
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-3">
                                                Aggregated, anonymised analytics
                                            </td>
                                            <td className="p-3">Indefinite</td>
                                            <td className="p-3">
                                                Non-personal; cannot identify any individual
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <p className="mt-3 text-xs sm:text-sm text-gray-600">
                                Archived records are encrypted, access-controlled, and cannot
                                be used to contact you or identify you for any commercial
                                purpose.
                            </p>
                        </section>

                        {/* Section 6 – Timeline */}
                        <section id="timeline" className="scroll-mt-28">
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                6. Processing Timeline
                            </h2>
                            <ul className="list-disc list-inside space-y-2 text-gray-700">
                                <li>
                                    <strong>Within 48 hours:</strong> Acknowledgment email sent
                                    confirming receipt of your request.
                                </li>
                                <li>
                                    <strong>Within 7 days:</strong> Identity verified and account
                                    deactivated (login disabled).
                                </li>
                                <li>
                                    <strong>Within 30 days:</strong> All personal identifiers
                                    deleted from active databases and backup systems.
                                </li>
                            </ul>
                            <p className="mt-3 text-xs sm:text-sm text-gray-600">
                                If we are unable to process your request due to an ongoing legal
                                obligation or dispute, we will inform you within 48 hours and
                                explain the reason for the delay.
                            </p>
                        </section>

                        {/* Section 7 – Contact */}
                        <section
                            id="contact"
                            className="scroll-mt-28 border-t border-gray-200 pt-6"
                        >
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                7. Contact &amp; Support
                            </h2>
                            <p className="text-sm text-gray-600 mb-3">
                                For any questions regarding your deletion request or data
                                handling, please contact our Grievance Redressal Officer:
                            </p>
                            <div className="border border-gray-200 rounded-lg p-4 space-y-1 text-sm text-gray-800">
                                <p>
                                    <strong>Designation:</strong> Grievance Redressal Officer
                                </p>
                                <p>
                                    <strong>Entity:</strong> Fixezy Private Limited
                                </p>
                                <p>
                                    <strong>Address:</strong> Vijayanagar, Bengaluru, Karnataka –
                                    560040, India
                                </p>
                                <p>
                                    <strong>Email:</strong>{" "}
                                    <a
                                        href="mailto:support@fixezy.in"
                                        className="text-blue-600 hover:underline"
                                    >
                                        support@fixezy.in
                                    </a>
                                </p>
                                <p>
                                    <strong>Working Hours:</strong> Mon – Fri, 9:00 AM – 6:00 PM
                                    IST
                                </p>
                                <p className="text-xs text-gray-500 pt-1">
                                    Grievances are acknowledged within 48 hours and resolved
                                    within 30 days.
                                </p>
                            </div>
                        </section>
                    </main>
                </div>
            </div>

            <Footer />
        </div>
    );
}
